import { AppNode, DetailPagePlannerData, NodeDataByType, NodeType, NodeStatus, PanoramaQuality, PanoramaQualityDimensions } from '../types';
import { ScriptData, Shot, ScriptAsset, AssetKind, readScript, ScriptShotGenRef } from '../types/scriptNode';
import { generateApi, GenerateRequest, GenerateResult, TaskStatusResult } from '../services/generateApi';
import { isBackendAssetUrl, saveAssets, createMediaUpdate, saveSingleAsset, normalizeAssetUrl } from '../utils/assetUtils';
import { synthesizeSpeech, uploadAudioAsset } from './audioApi';
import { API_BASE } from '../config';
import { toast } from 'sonner';
import { editingApi, type ExportPosition } from './editingApi';
import { createCompositeVideoNode } from '../utils/smartEditNodeBridge';

/** Topaz 高清放大常量收口（单一数据源）：模型 ID / 倍数档位 / 默认子模型分别在 models.json、buildRequest 兜底、
 *  GenerateNode 派生节点、modelStore FALLBACK / EditorPanel / ParamSelectors 多处使用，一律引用此处导出，禁止散落裸字符串 */
export const TOPAZ_MODEL_ID = 'replicate-topaz-image-upscale';
export const UPSCALE_FACTORS = [2, 4, 6] as const;
export const UPSCALE_RESOLUTIONS = ['2k', '4k', '6k'];
export const DEFAULT_UPSCALE_SUBMODEL = '通用';
/** 倍数归一化：UPSCALE 节点可能由普通生图派生（'1k'）或为历史数据（'8k'），统一收敛到 2k/4k/6k（'8k'→'6k'） */
export const normalizeUpscaleResolution = (r?: string) =>
    r === '4k' || r === '6k' ? r : r === '8k' ? '6k' : '2k';
/** 分辨率档 → Replicate upscale_factor 枚举（归一化后必有值） */
export const UPSCALE_FACTOR_MAP: Record<string, string> = { '2k': '2x', '4k': '4x', '6k': '6x' };
/** 面板中文子模型选项 → Topaz enhance_model 枚举（带空格、大小写敏感，透传不转义）；人像档附加 face_enhancement: true */
export const UPSCALE_SUBMODEL_MAP: Record<string, string> = {
    '通用': 'Standard V2',
    '人像': 'Standard V2',
    '风景': 'High Fidelity V2',
    '建筑': 'High Fidelity V2',
    '动漫': 'CGI',
    '低分辨率修复': 'Low Resolution V2',
    '文字/Logo': 'Text Refine',
};
/** 人像档判别常量（§6.2/§11-R4：face_enhancement 开启档，强度滑块仅此档展示） */
export const UPSCALE_PORTRAIT_SUBMODEL = '人像';
/** face_enhancement_strength 默认值（Replicate input schema 默认 0.8，仅人像档生效） */
export const DEFAULT_UPSCALE_STRENGTH = 0.8;
/** §7.1 成本防护阈值（输出 MP）：超过阻断提交（Replicate 计费上限），超过二次确认 */
export const UPSCALE_MP_BLOCK_LIMIT = 512;
export const UPSCALE_MP_CONFIRM_LIMIT = 100;
/** 输出 MP 估算（§7.1）：输出MP = 输入宽 × 输入高 × factor² ÷ 1e6（factor 为倍数数值，如 2/4/6） */
export const estimateUpscaleOutputMp = (width: number, height: number, factor: number): number =>
    (width * height * factor * factor) / 1e6;
/** §2.4 计费阶梯锚点（输出 MP → 单次费用 $）：仅用于 >100MP 二次确认的费用档位展示，实际以 Replicate 账单为准 */
const UPSCALE_MP_COST_TIERS: Array<[number, number]> = [
    [12, 0.05], [24, 0.05], [36, 0.10], [48, 0.10], [60, 0.15],
    [96, 0.20], [132, 0.24], [168, 0.29], [336, 0.53], [512, 0.82],
];
/** 按阶梯锚点线性插值估算单次费用（≤12MP 按 $0.05 起价，>512MP 已被阻断不会进入估算） */
export const estimateUpscaleCost = (mp: number): number => {
    const last = UPSCALE_MP_COST_TIERS[UPSCALE_MP_COST_TIERS.length - 1];
    if (mp <= UPSCALE_MP_COST_TIERS[0][0]) return UPSCALE_MP_COST_TIERS[0][1];
    if (mp >= last[0]) return last[1];
    for (let i = 1; i < UPSCALE_MP_COST_TIERS.length; i++) {
        const [mp1, c1] = UPSCALE_MP_COST_TIERS[i];
        if (mp <= mp1) {
            const [mp0, c0] = UPSCALE_MP_COST_TIERS[i - 1];
            return c0 + ((mp - mp0) / (mp1 - mp0)) * (c1 - c0);
        }
    }
    return last[1];
};

/** 收集 img2img 输入图：常规节点读 data.image；LAYER_EDIT 读保存时合成的输出资产 outputImageUrl（导出供单测，见 §8 节点链路） */
export const collectInputImages = (inputs: AppNode[]): string[] => {
    const images: string[] = [];
    inputs.forEach(n => {
        if (!n?.data) return;
        if (n.data.image) { images.push(n.data.image); return; }
        if (n.type === NodeType.LAYER_EDIT) {
            const outputImageUrl = (n.data as NodeDataByType<NodeType.LAYER_EDIT>).outputImageUrl;
            if (outputImageUrl) images.push(outputImageUrl);
        }
    });
    return images;
};

/** v1.4.85 分镜锚定资产图解析：分镜生图节点 data.scriptShotRef 记录锚定资产 id 顺序表，
 *  经上游连线脚本节点实时读取 script.assets 按序解析 imageUrl（资产已删除/未出图的跳过）。
 *  顺序与 ScriptShotGenRef.assetIds 严格一致（提示词锚定区块 v1.4.98 起为名字式
 *  「@资产名」，与本函数无文本耦合）；返回空数组 = 无有效锚定图（调用方回退常规 img2img 输入） */
export const collectShotAnchorImages = (node: AppNode, inputs: AppNode[]): string[] => {
    const ref = (node.data as { scriptShotRef?: ScriptShotGenRef }).scriptShotRef;
    if (!ref?.scriptNodeId || !Array.isArray(ref.assetIds) || ref.assetIds.length === 0) return [];
    const scriptNode = inputs.find(n => n.id === ref.scriptNodeId && n.type === NodeType.SCRIPT_NODE);
    if (!scriptNode) return [];
    const script = readScript((scriptNode.data as { script?: unknown }).script);
    if (!script) return [];
    return ref.assetIds
        .map(id => script.assets.find(a => a.id === id)?.imageUrl)
        .filter((url): url is string => !!url);
};

/** 详情页节点作为上游时的产品参考图节点：面板管理（productImageNodeIds）+ 手动连线接入（connectedProductImageNodeIds）；
 *  竞品图默认仅用于竞品分析不传给下游生图，避免竞品风格污染下游生成结果（开启竞品风格参考出图时除外，见 collectDetailPageStyleReferences） */
export const collectDetailPageProductImages = (detailPage: AppNode, allNodes: AppNode[]): AppNode[] => {
    const planner = (detailPage.data as NodeDataByType<NodeType.DETAIL_PAGE>).planner;
    if (!planner) return [];
    const productIds = [...(planner.productImageNodeIds ?? []), ...(planner.connectedProductImageNodeIds ?? [])];
    return productIds
        .map(id => allNodes.find(n => n.id === id))
        .filter((n): n is AppNode => !!n && !!n.data.image);
};

/** 详情页节点作为上游时的竞品风格参考图节点（P2 图片通道，默认关防串品）：仅当 useCompetitorStyleReference 开启且未跳过竞品时收集；
 *  竞品参考 ≤2 张、产品图 ≥3 张时压至 1 张（多图融合下单图影响力被稀释，产品保真优先）；与 collectDetailPageProductImages 对称 */
export const collectDetailPageStyleReferences = (detailPage: AppNode, allNodes: AppNode[]): AppNode[] => {
    const planner = (detailPage.data as NodeDataByType<NodeType.DETAIL_PAGE>).planner;
    if (!planner?.useCompetitorStyleReference || planner.skipCompetitor) return [];
    const productCount = [...(planner.productImageNodeIds ?? []), ...(planner.connectedProductImageNodeIds ?? [])].length;
    const maxRefs = productCount >= 3 ? 1 : 2;
    const competitorIds = [...(planner.competitorImageNodeIds ?? []), ...(planner.connectedCompetitorImageNodeIds ?? [])];
    return competitorIds
        .map(id => allNodes.find(n => n.id === id))
        .filter((n): n is AppNode => !!n && !!n.data.image)
        .slice(0, maxRefs);
};

/** 区块身份 → 竞品屏 moduleType 语义映射关键词表（P2.5 §3.3-5：首屏↔品牌首屏、卖点区块↔核心卖点屏…）；
 *  顺序即匹配优先级（区块标题多关键词命中时取靠前的更具体项） */
const BLOCK_MODULE_KEYWORDS: Array<{ moduleType: string; keywords: string[] }> = [
  { moduleType: '品牌首屏', keywords: ['首屏', '主视觉', '头图'] },
  { moduleType: '痛点共鸣', keywords: ['痛点'] },
  { moduleType: '核心卖点', keywords: ['卖点'] },
  { moduleType: '使用场景', keywords: ['场景'] },
  { moduleType: '品质背书', keywords: ['背书', '资质', '认证', '检测', '专利'] },
  { moduleType: '用户评价', keywords: ['评价', '口碑', '买家'] },
  { moduleType: '服务保障', keywords: ['保障', '售后', '物流'] },
  { moduleType: '产品参数', keywords: ['参数', '规格', '尺寸'] },
];

/** P2.5 区块单元类型（S=首屏 D=详情 T=尾屏）→ 兜底 moduleType 候选（按序取首个有屏者）：
 *  S 首屏语义无歧义直接锁定「品牌首屏」；T 尾屏兜底「产品参数/服务保障」；D 详情中段内容多样仅走文本匹配 */
const UNIT_TYPE_MODULE_FALLBACK: Record<'S' | 'D' | 'T', string[]> = {
  S: ['品牌首屏'],
  D: [],
  T: ['产品参数', '服务保障'],
};

/** 候选 moduleType 有序解析：unitType 结构化身份优先（文案标题不可靠，如「第一区块·夏日随行治愈风」
 *  无关键词、prompt 却可能误中「场景」），文本关键词匹配次之（D 主信号 / T 命中更具体模块时） */
const resolveBlockModuleTypes = (blockIdentity: string, unitType?: 'S' | 'D' | 'T'): string[] => {
    const fallbacks = unitType ? UNIT_TYPE_MODULE_FALLBACK[unitType] : [];
    if (fallbacks.length === 1) return fallbacks;
    const s = (blockIdentity || '').trim();
    if (s) {
        for (const { moduleType, keywords } of BLOCK_MODULE_KEYWORDS) {
            if (keywords.some(k => s.includes(k))) return [moduleType];
        }
    }
    return fallbacks;
};

/** 区块身份（出图节点标题「第N区块·标题」+ 提示词前缀）→ 竞品屏 moduleType（无命中返回 null，调用方回落整图模式）；导出供单测 */
export const resolveBlockModuleType = (blockIdentity: string, unitType?: 'S' | 'D' | 'T'): string | null =>
    resolveBlockModuleTypes(blockIdentity, unitType)[0] ?? null;

/** P2.5 屏级风格参考项：url 作参考图输入，styleNotes（该屏版式要点）注入出图提示词补足文字版式约束 */
export interface DetailPageScreenStyleRef {
    url: string;
    moduleType: string;
    styleNotes?: string;
}

/** P2.5 区块级语义映射选屏（结构化版）：切屏产物存在且区块身份命中 moduleType 时返回对应屏切块
 *  （URL + 该屏版式要点，替代整图参考，参考噪声大降）；未切屏 / 未开开关 / 无语义命中 → 空（调用方回落整图模式） */
export const collectDetailPageScreenStyleRefItems = (planner: DetailPagePlannerData | undefined | null, blockIdentity: string, unitType?: 'S' | 'D' | 'T'): DetailPageScreenStyleRef[] => {
    if (!planner?.useCompetitorStyleReference || planner.skipCompetitor) return [];
    const screens = planner.competitorScreens ?? [];
    if (screens.length === 0) return [];
    const moduleTypes = resolveBlockModuleTypes(blockIdentity, unitType);
    if (moduleTypes.length === 0) return [];
    const productCount = [...(planner.productImageNodeIds ?? []), ...(planner.connectedProductImageNodeIds ?? [])].length;
    const maxRefs = productCount >= 3 ? 1 : 2;
    for (const moduleType of moduleTypes) {
        const matched = screens
            .filter(s => (s.moduleType || '').trim() === moduleType)
            .sort((a, b) => a.index - b.index)
            .slice(0, maxRefs)
            .map(s => ({ url: s.url, moduleType: s.moduleType, styleNotes: s.styleNotes }));
        if (matched.length > 0) return matched;
    }
    return [];
};

/** 屏级语义选屏（URL 简化版，供仅需要参考图地址的场景与既有单测） */
export const collectDetailPageScreenStyleRefs = (planner: DetailPagePlannerData | undefined | null, blockIdentity: string, unitType?: 'S' | 'D' | 'T'): string[] =>
    collectDetailPageScreenStyleRefItems(planner, blockIdentity, unitType).map(i => i.url);

/** P2.5 竞品版式要点声明：图像参考的版式遵循远弱于文字描述，屏切块命中时把 LLM 标注的该屏版式要点
 *  注入提示词补足结构约束；声明仅借鉴版式结构，防照抄竞品文字与产品形态（无要点命中返回空串） */
export const buildScreenStyleNotesDeclaration = (items: DetailPageScreenStyleRef[]): string => {
    const withNotes = items.filter(i => (i.styleNotes || '').trim());
    if (withNotes.length === 0) return '';
    const lines = withNotes.map(i => `- ${i.moduleType}：${(i.styleNotes || '').trim()}`);
    return `\n【竞品版式参考要点】（与竞品风格参考图对应：仅借鉴其版式结构与信息层级，文字文案、产品主体仍严格按本提示词执行，禁止照抄竞品文字与产品形态）\n${lines.join('\n')}`;
};

/** 详情页上游是否请求竞品风格参考（开关开启且未跳过竞品）：gemini 降级 toast 判定用 */
export const detailPageRequestsStyleReference = (detailPage: AppNode): boolean => {
    const planner = (detailPage.data as NodeDataByType<NodeType.DETAIL_PAGE>).planner;
    return !!planner?.useCompetitorStyleReference && !planner.skipCompetitor;
};

/** 竞品风格参考角色声明（P2）：竞品参考图存在时拼到 prompt 尾部（正向白名单 + 反向黑名单 + 产品保真，防串品/文字污染/版面照抄）；
 *  generateBlockImage 创建出图节点后经 orchestrator 统一链路触发，与画布手动生成同源调用 */
export const buildStyleReferenceDeclaration = (styleRefCount: number): string => {
    if (styleRefCount <= 0) return '';
    return `\n【参考图角色声明】输入图片中最后 ${styleRefCount} 张为竞品风格参考图（其余为本次产品参考图）：风格参考图仅用于参考其配色、版式密度、质感光影等风格维度；严禁出现参考图中的产品形态、品牌标识/LOGO 与文字文案；产品主体必须严格保持本品参考图中的外观，不得融合或改绘。`;
};

/** 实拍质感锚定声明（P1 去 AI 味兜底）：详情页区块出图时注入，对齐 page_prompt guide 3.3.1 五条细则（光影/材质/景深/用词/画质）；
 *  渲染类风格（C4D 素模/科技可视化）由豁免条款让位；画布独立生图（插画/3D 场景）不注入，避免污染风格自由度 */
export const buildPhotorealisticDeclaration = (): string =>
    '\n【实拍质感锚定】（若本提示词明确指定 3D渲染/C4D/科技可视化等渲染类风格，则本条让位于该风格）最终画面默认实拍摄影质感：光影为自然光源与柔和过渡（窗光/环境光/自然投影），禁止无物理来源的发光/光效/光晕堆砌；材质呈现真实肌理（布纹/木纹/陶瓷釉面/金属拉丝/水珠），表面细节可感知；景深虚实自然（主体清晰、背景柔和虚化）有空气感；禁用"摄影级渲染"类矛盾词组；禁止只堆 8K/超高清等分辨率词，质感来源优先。';

export interface GenerationContext {
    projectId: string | null;
    onProgress: (id: string, data: Partial<AppNode['data']>) => void;
    onAssetGenerated: (type: 'image' | 'video' | 'audio', src: string, title: string, prompt?: string) => void;
    onTaskCreated: (taskId: string, nodeId: string, nodeType: string, request: Record<string, any>) => void;
}

export interface GenerationStrategy {
    canHandle: (nodeType: NodeType) => boolean;
    buildRequest: (node: AppNode, inputs: AppNode[], prompt: string) => GenerateRequest;
    processResult: (node: AppNode, result: GenerateResult, ctx: GenerationContext, prompt: string) => Promise<Partial<AppNode['data']>>;
    /** 同步 REST 通道（TTS 等不走异步任务流的后端接口）：存在时 executeGeneration 直接调用并跳过 buildRequest/pollTask 流程 */
    executeSync?: (node: AppNode, inputs: AppNode[], prompt: string, ctx: GenerationContext) => Promise<Partial<AppNode['data']>>;
}

const imageGeneratorStrategy: GenerationStrategy = {
    canHandle: (t) => t === NodeType.IMAGE_GENERATOR,
    buildRequest: (node, inputs, prompt) => {
        const inputImages = collectInputImages(inputs);
        // v1.4.85 分镜锚定资产图：scriptShotRef 存在时经上游脚本节点按「@图片N」顺序解析注入；
        // 解析为空（资产删除/未出图）回退常规 img2img 输入，均无则纯文生图
        const anchorImages = collectShotAnchorImages(node, inputs);
        const finalInputImages = anchorImages.length > 0 ? anchorImages : inputImages;
        const hasImageInput = finalInputImages.length > 0;
        const isUpscale = node.data.generationMode === 'UPSCALE';
        const upResolution = normalizeUpscaleResolution(node.data.resolution);
        // v1.4.56 脚本资产图生成（scriptAssetRef 携带面板所选画质档）透传 params.quality；画布常规生图无画质概念不携带
        const assetQuality = (node.data as { scriptAssetRef?: { quality?: string } }).scriptAssetRef?.quality;
        // v1.4.84 分镜批量生图：确认面板所选画质档直写节点 data.quality（常规画布节点无此字段不受影响）
        const batchQuality = (node.data as { quality?: string }).quality;

        return {
            modelId: isUpscale
                ? (node.data.model || TOPAZ_MODEL_ID)
                : (node.data.model || 'grsai-gpt-image-2'),
            prompt: prompt || '\u8FDB\u884C\u521B\u4F5C',
            mode: hasImageInput ? 'image-to-image' : 'text-to-image',
            inputImages: hasImageInput ? finalInputImages : undefined,
            params: isUpscale ? {
                resolution: upResolution,
                upscaleFactor: UPSCALE_FACTOR_MAP[upResolution],
                upscaleSubModel: UPSCALE_SUBMODEL_MAP[node.data.upscaleSubModel || DEFAULT_UPSCALE_SUBMODEL],
                upscaleStrength: node.data.upscaleStrength,
                faceEnhancement: node.data.upscaleSubModel === '人像',
            } : {
                aspectRatio: node.data.aspectRatio || '16:9',
                resolution: node.data.resolution || '1k',
                imageCount: node.data.imageCount || 1,
                ...(assetQuality ? { quality: assetQuality } : batchQuality ? { quality: batchQuality } : {}),
            },
        };
    },
    processResult: async (node, result, ctx, prompt) => {
        if (!result.results || result.results.length === 0) {
            throw new Error('\u672A\u6536\u5230\u751F\u6210\u7684\u56FE\u7247');
        }

        const urls = result.results.map(r => r.url).filter((u): u is string => !!u);
        // v1.4.85 分镜锚定节点（scriptShotRef 存在）实际经锚定资产图走 i2i，metadata 同步标注
        const hasImageInput = node.data.generationMode === 'IMG2IMG' || node.data.generationMode === 'UPSCALE'
            || !!(node.data as { scriptShotRef?: ScriptShotGenRef }).scriptShotRef;

        const { savedUrls } = await saveAssets(ctx.projectId, urls, {
            nodeId: node.id,
            prefix: 'image',
            metadata: {
                generator_node_id: node.id,
                generator_type: hasImageInput ? 'i2i' : 't2i',
                prompt,
                model: node.data.model || '',
            },
        });

        return createMediaUpdate(savedUrls, false);
    }
};

const videoGeneratorStrategy: GenerationStrategy = {
    canHandle: (t) => t === NodeType.VIDEO_GENERATOR,
    buildRequest: (node, inputs, prompt) => {
        const inputImages = collectInputImages(inputs);
        const hasImageInput = inputImages.length > 0;
        // v1.4.97 分镜锚定资产图（对标 imageGeneratorStrategy v1.4.85）：scriptShotRef
        // 经上游脚本节点按 assetIds 顺序解析注入 referenceImages 独立参考通道；
        // 不并入 inputImages——图生视频适配器对多图有歧义语义（grsai 仅取首图、
        // Kling 第二图当尾帧、Agnes 多图进关键帧模式），首帧通道必须保持纯净；
        // 后端 GenerateRequest.referenceImages 已预留同名字段，img_ref 语义适配器后续接入
        const anchorImages = collectShotAnchorImages(node, inputs);

        return {
            modelId: node.data.model || 'grsai-minimax-h3',
            prompt: prompt || '\u751F\u6210\u4E00\u6BB5\u89C6\u9891',
            mode: hasImageInput ? 'image-to-video' : 'text-to-video',
            inputImages: hasImageInput ? inputImages : undefined,
            referenceImages: anchorImages.length > 0 ? anchorImages : undefined,
            params: {
                aspectRatio: node.data.aspectRatio || '16:9',
                duration: node.data.duration || 5,
                videoCount: node.data.videoCount || 1,
                generationMode: node.data.generationMode,
                resolution: node.data.resolution,
                generateAudio: node.data.generateAudio,
            },
        };
    },
    processResult: async (node, result, ctx, prompt) => {
        let updates: Partial<AppNode['data']> = {};

        if (result.error) {
            updates = { error: result.error };
        } else if (result.results && result.results.length > 0) {
            const videoResult = result.results[0]!;
            if (videoResult.outputType === 'image') {
                const finalUrl = await saveSingleAsset(ctx.projectId, videoResult.url || '', 'fallback_image.png', {
                    generator_node_id: node.id,
                    generator_type: 'video_fallback',
                    prompt,
                    model: node.data.model || '',
                });
                updates = { image: finalUrl, videoUri: undefined, error: "Region restricted: Generated preview image instead." };
            } else {
                const finalUrl = await saveSingleAsset(ctx.projectId, videoResult.url || '', 'video.mp4', {
                    generator_node_id: node.id,
                    generator_type: 'video',
                    prompt,
                    model: node.data.model || '',
                    generation_mode: node.data.generationMode || '',
                }, true);
                updates = { videoUri: finalUrl, videoMetadata: result.videoMetadata };
            }
        } else {
            // 视频任务完成但没有结果 URL
            updates = { error: '视频生成完成但未返回视频结果，请重试或更换模型' };
        }

        if (node.data.generateAudio && result.results) {
            const audioResult = result.results.find(r => r.outputType === 'audio');
            if (audioResult) {
                const finalAudioUrl = await saveSingleAsset(ctx.projectId, audioResult.url || '', 'audio.wav', {
                    generator_node_id: node.id,
                    generator_type: 'audio',
                    prompt,
                });
                updates.audioUri = finalAudioUrl;
            }
        }

        return updates;
    }
};

const audioGeneratorStrategy: GenerationStrategy = {
    canHandle: (t) => t === NodeType.AUDIO_GENERATOR,
    buildRequest: (node, inputs, prompt) => {
        return {
            modelId: node.data.model || '',
            prompt: prompt || '\u751F\u6210\u97F3\u9891',
            mode: 'text-to-audio',
            params: {
                duration: node.data.duration,
            },
        };
    },
    // TTS 走同步 REST /api/audio/tts（AudioService/grsai），不经异步任务流
    executeSync: async (node, inputs, prompt, ctx) => {
        const text = prompt || '\u751F\u6210\u4E00\u6BB5\u7075\u611F\u97F3\u4E50';
        const audioDataUrl = await synthesizeSpeech(text, 'Kore');

        if (ctx.projectId) {
            const backendUrl = await uploadAudioAsset(ctx.projectId, audioDataUrl, node.id, text);
            return { audioUri: normalizeAssetUrl(backendUrl) };
        }
        // 无项目上下文时退回 data URL（不入库）
        return { audioUri: audioDataUrl };
    },
    processResult: async () => {
        // executeSync 已处理，异步任务流不会到达此处
        return {};
    }
};

const imageEditorStrategy: GenerationStrategy = {
    canHandle: (t) => t === NodeType.IMAGE_EDITOR,
    buildRequest: (node, inputs, prompt) => {
        const inputImages = collectInputImages(inputs);
        const img = node.data.image || inputImages[0];

        return {
            modelId: node.data.model || 'grsai-nano-banana-pro',
            prompt: prompt || '\u7F16\u8F91\u56FE\u7247',
            mode: 'image-editing',
            inputImages: img ? [img] : undefined,
            params: {},
        };
    },
    processResult: async (node, result, ctx, prompt) => {
        if (!result.results || result.results.length === 0) {
            throw new Error('\u672A\u6536\u5230\u7F16\u8F91\u540E\u7684\u56FE\u7247');
        }

        const finalUrl = await saveSingleAsset(ctx.projectId, result.results[0]?.url || '', 'edited_image.png', {
            generator_node_id: node.id,
            generator_type: 'image_editor',
            prompt,
            model: node.data.model || '',
        });

        return { image: finalUrl };
    }
};

const panoramaGeneratorStrategy: GenerationStrategy = {
    canHandle: (t) => t === NodeType.PANORAMA_GENERATOR,
    buildRequest: (node, inputs, prompt) => {
        const inputImages = collectInputImages(inputs);
        const referenceImage = node.data.image || inputImages[0];

        return {
            modelId: node.data.model || 'grsai-nano-banana-pro',
            prompt: prompt || '\u751F\u6210\u4E00\u5F20720\u00B0\u5168\u666F\u56FE',
            mode: referenceImage ? 'img2panorama' : 'txt2panorama',
            inputImages: referenceImage ? [referenceImage] : undefined,
            params: {
                quality: node.data.panoramaQuality || 'hd',
                aspectRatio: '2:1',
            },
        };
    },
    processResult: async (node, result, ctx, prompt) => {
        if (!result.results || result.results.length === 0) {
            throw new Error('\u672A\u6536\u5230\u751F\u6210\u7684\u5168\u666F\u56FE');
        }

        let finalUrl: string = result.results[0]?.url || '';
        if (!finalUrl) throw new Error('\u672A\u6536\u5230\u751F\u6210\u7684\u5168\u666F\u56FE');
        if (ctx.projectId && !isBackendAssetUrl(finalUrl)) {
            finalUrl = await saveSingleAsset(ctx.projectId, finalUrl, 'panorama.png', {
                generator_node_id: node.id,
                generator_type: 'panorama',
                prompt,
                model: node.data.model || '',
            });
        }

        ctx.onAssetGenerated('image', finalUrl, prompt);

        return {
            image: finalUrl,
            panoramaOutputUrl: finalUrl,
            panoramaOutputDimensions: PanoramaQualityDimensions[node.data.panoramaQuality || PanoramaQuality.HD],
            isPanoramaPreviewOpen: true,
        };
    }
};

// 脚本节点策略（脚本节点开发方案 §4.3）：走 LLM 对话通道（mode=chat，modelId 留空回退后端默认），
// 后端 feature 分支回传 result.data = { shots, entities }，此处防御解析为 ScriptData 写回 data.script；
// 骨架字段（source/title）由 useGenerateNodeActions 在触发生成前写入 node.data.script，生成完成后保留
// 拆镜规范（v1.4.34 重写）：原「每镜头只描述一个连续动作」迫使 LLM 输出孤立的氛围碎片，镜头间脱节跳跃、
// 描述不清晰（用户对比竞品反馈）。收敛为「主线完整 + 镜间衔接 + 叙事句」三重约束；同时保留硬约束
// （dialogue 固定格式供资产兜底提取解析说话人；时长口径 v1.4.73 起放开为 3s~15s 五档，见第 6 条）
// v1.4.73 时长口径放开（用户反馈）：原第 6 条「每项 duration 固定填 3s 或 5s」与视频生成 3s/5s 档位
// 对齐，但把分镜卡死在快节奏档位——对话交锋、情绪铺垫类镜头 5s 装不下（用户实测分镜表时长列清一色 5s）。
// 放开为 3s/5s/8s/10s/15s 五档按剧情节奏选择（关键动作/转折/突发事件短镜，对话/铺垫/氛围渲染/多人
// 走位长镜，最长 15s）；下游全链路天然兼容零改动：时长列自由输入无校验，合并提示词 splitDurationSegments
// 按任意数字解析并 ≤4 秒切段（15s → 0-4/4-8/8-12/12-15 四段）
// v1.4.38 补「关键事件必入镜 + 因果链完整」两条硬约束：用户实测剧本开篇"某年某月，太史令被御林军押走"
// 这类时间地点叙事句被 LLM 当作背景交代跳过（只把时间挪进后续主角镜头），导致主角后续惊惧反应无因之果
// （用户："没了这个镜头，我都不知道顾星澜为啥事情紧张"）——明确此类开篇事件句必须独立成镜
// v1.4.49 硬化实体约束（规则 5/8）：用户实测 14 镜脚本仅识别出 3 角色/2 场景/1 道具、画面描述实体高亮大面积
// 漏标——原规则 8「scenes/props 取自镜头描述」无完备性要求，LLM 只列主要实体；规则 5 未约束描述用词与
// entities 名称一致，ShotsTable 按资产名精确匹配的高亮随之漏标。现要求全量枚举 + 名称与描述用词一致
// v1.4.74 拆镜密度收敛（用户反馈）：剧本实测分镜过多（14 镜仅覆盖前半段，照此全片将 20+ 镜）——原规则 2
// 「每个事件至少对应一个镜头」+ 规则 6「每个场景通常拆 2-4 个镜头」双重加密，LLM 倾向一个小动作一镜。
// 规则 6 改「叙事节拍归并」拆镜法：同场景内因果连续的动作与对话合为一个节拍、一节拍一镜、节拍内小动作
// 合写进 description，仅场景切换/时间明显跳跃才另起新镜，并给「删掉某镜剧情仍连贯则应并入相邻镜」的
// 合并判断标准，删除「每场景 2-4 镜」密度指导；规则 2 同步改「必须被至少一个镜头拍到（时间相邻的连续
// 事件可合入同一镜头）」——保留 v1.4.38 防跳事件意图（开篇事件句硬约束原样保留），防止收敛密度后回退
// v1.4.75 全篇覆盖 + 实体全量提取（用户对比参考产品反馈）：实测 11 镜只拍到书房取证段，剧本后半段（暗号
// 解码/日食布局/结局）全丢；实体仅 3 角色/4 场景/2 道具——摄政王手令（仅在对白出现）、御林军（开篇群体）、
// 萧景琛/大明宫正殿（只出现在未拍到的后半段）全部漏识别。根因：①规则 1 无首尾覆盖硬约束，且每镜描述
// 过长耗尽输出预算，LLM 写到中段即截断；②规则 8 把实体来源锁死在「镜头描述中出现」，对白提及/未拍到的
// 关键实体全漏。现规则 1 加「首镜拍开头事件、末镜拍到结局、节拍全有镜」硬约束，规则 5 加 30-60 字精炼
// 篇幅约束与实体名称禁加修饰前缀，规则 8 改双来源全量提取（剧本原文 ∪ 镜头描述）
// v1.4.76 实体别名归一 + 原文可寻校验（用户对比实测反馈）：实测剧本实体「萧景琛」在镜头描述中被
// 写成职务尊称「摄政王」（原文出现 13 次），按资产名精确匹配的高亮与「未出场」判定全部漏标；
// 另有场景名「朝堂大殿」剧本原文出现 0 次（LLM 编造的幻觉名），资产卡却当作正常实体展示。
// ①规则 5 允许使用 entities.aliases 已登记的别称（词典匹配口径同步扩为 name+aliases）；
// ②规则 8 要求每项输出 aliases 数组登记全部其他称谓、name 必须能在剧本原文中直接找到（防幻觉名）；
// ③processResult.toAssets 解析 aliases（旧响应无此字段兜底空数组，ScriptAsset.aliases 可选兼容）
const SHOT_GUIDELINES = `拆镜规范：
1. 先通读全文把剧本从头到尾归并为叙事节拍序列（起因→发展→转折→结局），再按节拍序列拆镜：第一个镜头必须从剧本开头的第一个事件拍起，最后一个镜头必须拍到剧本结局，节拍序列中的每个节拍都必须有对应镜头，禁止只拍前半段就停止；所有镜头按时间顺序串起来必须能完整讲清整个故事，不遗漏关键情节、不凭空插入原文没有的支线；
2. 原文的每个关键剧情事件（人物被抓/押走/逃离/死亡、重大发现、重要决定、命运转折等）必须被至少一个镜头拍到（时间相邻的连续事件可合入同一镜头）：尤其开篇以时间地点开头的叙事句（如"某年某月，某人被御林军自府中押走"）承载的正是第一个关键事件，必须拍成实际镜头，禁止当作背景交代跳过，禁止只把时间信息挪进后续镜头而丢掉事件本身；
3. 保持因果链完整：后续镜头中角色出现强烈情绪反应（惊惧、奔逃、痛哭等）时，前序镜头必须已拍出导致该反应的起因事件，观众按镜头顺序看完能明白角色为何如此；
4. 相邻镜头必须前后衔接：上一镜头动作的结束或角色的反应就是下一镜头的起点，同一场景内角色的位置、姿态、情绪从上一镜自然延续，不得连续出现无角色动作的空镜；
5. 每镜头 description 写成精炼的完整叙事句（30-60 字，为全部镜头完整输出留足篇幅）：点明场景与在场角色，写清角色正在做的具体动作及其意图或情绪反应，让读者不看原文也能明白发生了什么，禁止只堆砌环境氛围；描述中出现的角色、场景、道具一律使用 entities 中登记的名称或其 aliases 中已登记的别称（界面按名称与别称匹配高亮实体），除已登记的名称与别称外不得用其他代称、绰号或改写，也不得给实体名称添加修饰前缀（如 entities 中叫"司天台"就不得写成"大明宫司天台"）；
6. 每个镜头聚焦单一场景内的一段连续时间，一镜不跨场景。拆镜以「用最少的镜头讲清剧情」为目标：先把剧本归并为叙事节拍——同一场景内因果连续的一段动作与对话合为一个节拍，一个节拍拍一镜，节拍内的连续小动作（如蹲身摸索、取出文稿、展开细读）合写进同一镜的 description，不要一个小动作拆一镜；仅当场景切换或时间明显跳跃时才另起新镜。镜头宁少勿多：若删掉某镜后剧情因果仍完整连贯，说明该镜内容应并入相邻镜头。每项 duration 按剧情节奏从 3s/5s/8s/10s/15s 五档中选择：关键动作、转折、突发事件用短镜（3s/5s），对话交锋、情绪铺垫、氛围渲染、节拍内多动作长流程用长镜（8s/10s/15s），最长不超过 15s；
7. dialogue 有台词时固定按「角色名：台词」格式书写，无台词填空字符串；
8. entities 必须覆盖剧本原文与全部镜头，逐类枚举不得遗漏：characters 列出剧本中出现的全部有名有姓角色（包括仅在对白、密信或他人转述中被提及的关键人物）与成建制的群体角色（如开篇押人的官兵、传旨守门的内侍），并覆盖 dialogue 全部说话人；scenes 列出剧情涉足或明确提及的全部场景（包括仅被提及的关键地点）；props 列出承担剧情功能的关键道具（包括仅在对白中被提及的物件，如奉旨查封的手令，以及反复出现的关键物件，如账簿副本、饰品珠子）；每项名称与剧本原文用词严格一致，且必须能在剧本原文（含对白、密信等引文）中直接找到，严禁编造原文中不存在的实体名称或场景名；若同一实体在剧本中有多个称谓（如本名与职务尊称、全称与简称），name 填该实体最常用的正式名称，并把其余全部称谓登记进该项的 aliases 字符串数组（无别称则空数组）；镜头描述中出现实体时必须使用 name 或 aliases 中已登记的称谓。`;
// v1.4.47 角色描述结构规范（对齐参考产品角色设定图描述·用户参考图）：entities.characters 的
// description 原先无结构约束，LLM 只输出一句话人物简介，不足以支撑设定图生图。现固定七段式
// （身份/描述/服装/饰品/特征/构图规范/绘画风格），其中构图规范段为角色设定图四视图模板原文
// （要求 LLM 原样输出），随 useScriptAssets.generateOne 的「设定描述：」整段注入生图 prompt，
// 产出白底四视图角色设定图；场景/道具描述维持一句话不变。
const CHARACTER_DESC_GUIDELINES = `角色描述结构规范：entities.characters 每项的 description 必须按以下结构生成（各部分以「；」分隔连成一段完整文字，禁止只写一句话简介）：
1. 开头直接点明角色身份（如「太史局女官」），不得重复名称；
2. 「描述：」依次写性别、年龄、身高（以米为单位）、体型体态、发型与发色、脸型、眼瞳、肤色；
3. 「服装：」上装、下装、鞋履，写明颜色与形制，须符合剧本时代背景与角色身份；
4. 「饰品：」随身饰品，无则写「无」；
5. 「特征：」1-3 个最有辨识度的外貌或气质特征；
6. 「构图规范（必须包含）：」之后原样输出以下模板全文（含两端引号，一字不改）：「高质量专业角色设定图，横向构图，纯白色纯净背景，中性摄影棚灯光，平衡布局（必须是左侧视图）：正面头部特写（占图片水平 1/3 的空间）+ 全身正面照 + 全身左侧面视图 + 全身背面视图（占图片水平剩余的 2/3 的空间，并排排列），四个视图中间用淡灰色(#E2E2E2)的2px细线分割，无任何道具或背景物体，光影：中性摄影棚灯光，柔和的前侧光，清晰的角色定义，自然的肤色，面部清晰服装可辨识，平视镜头，完整全身，无裁剪。不得出现道具武器/食物/手持物/动物（角色必须空手）；不得出现复杂动作、情绪表情、面部遮挡；不得出现环境背景（仅白色）；不得出现其他角色；确保所有视图中的面部特征、发型、体型和服装保持一致；不得出现文字、水印、标签、UI元素；无背景场景，无过度风格化，纯 Naive 样貌。」；
7. 「绘画风格：」用一句话点明该角色画风，与 style 字段风格一致（如「盛唐宫廷古风悬疑写实真人电影级写实」）。
完整示例（构图规范段以「……」缩略，实际输出必须为上面模板全文）：太史局女官。描述：女，22岁，身高1.65米，纤细挺拔，高耸的唐代宫髻，发质顺滑，黑发，清秀的瓜子脸，深棕色眼瞳，白皙肤色；服装：淡青色圆领女官袍，白色中单，黑色窄头布靴；饰品：腰挂皮质算袋等；特征：手指修长，气质沉静冷静；构图规范（必须包含）：「……」；绘画风格：盛唐宫廷古风悬疑写实真人电影级写实。`;
// v1.4.48 场景与道具描述结构规范（对齐参考产品场景/道具设定图描述·用户参考文本）：entities.scenes 与
// entities.props 的 description 原先同样无结构约束，只输出一句话简介，不足以支撑设定图生图。场景固定
// 十段式（空间名称/空间是什么/空间类型/空间尺度/高度宽度/背景层/前景层/氛围/构图规范/视觉风格），
// 构图规范段为场景设定图 2×2 四大全景视图模板原文（空场景无人）；道具固定为「道具名称/外观特征/
// 材质细节描述/关键细节/六视图多角度展示模板/视觉风格」结构，展示段为道具 2×3 六视图模板原文。
// 两段模板均要求 LLM 原样输出，随 useScriptAssets.generateOne 的「设定描述：」整段注入生图 prompt，
// 分别产出空场景四视角设定图与白底六视图道具展示图；视觉风格段与 style 字段保持一致。
// v1.4.58 场景十段式指令强化（用户对比外部同类提示词反馈）：①「空间是什么」追加功能绑定特色细节引导
// （如观象台地面环形刻度线）——围绕用途提炼专属陈设/图纹，让同类场景拉开辨识度；②「背景层」改轮廓化
// 描写——大全景视距下远景只有轮廓像素，展开细节无效；③「前景层」加「中心主体 → 周边陈设 → 围合台地」
// 层次组织 + 大全景可见性约束——散落文具等微小物件在四大全景中不可见、纯浪费 token；完整示例同步更新。
const SCENE_DESC_GUIDELINES = `场景描述结构规范：entities.scenes 每项的 description 必须按以下结构生成（各部分连成一段完整文字，禁止只写一句话简介）：
1. 开头「空间名称：」写场景名称，「空间是什么：」用一句话点明该空间的用途属性（如「观象台」「关押死囚的天牢」），并围绕该用途写出 1-2 处功能绑定的特色陈设或地面图纹（如观象台地面刻有环形刻度线），让空间与同类场景拉开辨识度；
2. 「空间类型：」写空间类型（如「建筑前景 + 露天高台」「室内大殿」「街巷」）；「空间尺度：」写宏观/中景/微观等尺度感；「高度/大致宽度：」写明大致尺寸（如「高约15米，宽约20米」）；
3. 「背景层：」写远景背景的空间元素，用轮廓化描写点到为止（如「远处的宫殿群轮廓」），不展开细节；「前景层：」写近景前景的空间元素与核心陈设，按「中心主体 → 周边陈设 → 围合与台地」的层次组织（如「巨大的青铜浑天仪居中，四周白玉护栏与登台石阶」），只写大全景视距下清晰可辨的元素，禁止写散落的文具、首饰等微小物件（四大全景中不可见）；
4. 「氛围：」用两三个词概括空间氛围（如「肃穆宏大」「阴森压抑」）；
5. 「构图规范（固定话术，必须包含）：」之后原样输出以下模板全文（含两端引号，一字不改）：「高质量专业场景设定图，横向构图，以 2 行 2 列的干净网格四等分整齐排版，每个格子都是独立的 16:9 横向画面，展示同一场景的四个大全景视角（1为正面中心线大全景视图，镜头正对场景中心轴，构图严格居中，画面同时包含顶面与底面，尽量展示完整空间层次、更多环境细节和深景深；2以1的中心线为参考，摄像机移动到场景左前方45度位置的大全景视图，镜头仍对准场景核心区域；3为以1的中心线为参考，摄像机移动到场景右前方45度位置的大全景视图；4为镜头在室内最深处向外拍摄的正中心全景图。四个视角也可以是东南西北四个方位的视角。四个视角必须表现同一地点、同一时间、同一天气、同一光源、同一空间结构和同一美术风格。环境清晰，细节丰富，景深较深，光影自然，专业摄影，超清画质。不得出现任何人物（这是空场景参考图，必须空无一人），也不得出现人群、背影、剪影、人脸、手脚、人物倒影、人物影子、照片人物、屏幕人物、镜中人物、剧情事件、人物活动；不得让四个视角表现成四个不同场景；不得改变建筑结构、空间比例、主体位置、材质、色彩、天气、时间段或光源方向；画面构图不得倾斜、透视畸变、广角畸变、变形、扭曲；不得出现鱼眼视角、斜角、极端俯视、极端仰视；正面视图必须居中、对称、中心线构图；左前方 45 度、右前方 45 度和背后视角必须保持镜头稳定、空间连贯、比例一致；禁止模糊、低画质；禁止景深太浅；不得出现文字、水印、签名、边框、标签、UI元素、杂乱元素。」；
6. 结尾以「[视觉风格：xxx]」点明该场景画风，与 style 字段风格一致。
完整示例（构图规范段以「……」缩略，实际输出必须为上面模板全文）：空间名称：司天台，空间是什么：观象台。空间类型：建筑前景 + 露天高台，空间尺度：宏观，高度/大致宽度：高约15米，宽约20米。背景层：远处层叠的唐代宫廷殿宇轮廓，前景层：巨大的青铜浑天仪居中，台面刻有二十四节气环形刻度线，四周白玉护栏与登台石阶。氛围：肃穆宏大。构图规范（固定话术，必须包含）：「……」。[视觉风格：盛唐宫廷·古风悬疑写实·真人电影级写实]`;
const PROP_DESC_GUIDELINES = `道具描述结构规范：entities.props 每项的 description 必须按以下结构生成（各部分连成一段完整文字，禁止只写一句话简介）：
1. 开头「道具名称：」写道具名称；
2. 「外观特征：」写道具整体外形构成（形状、数量、组合方式）；
3. 「材质细节描述：」写材质、表面工艺、色泽包浆与具体尺寸；
4. 「关键细节：」写 1-2 处最有辨识度或与剧情相关的细节（含隐藏细节）；
5. 之后原样输出以下模板全文（一字不改）：高质量写实道具多角度展示图，横向构图，以 2 行 3 列的干净网格整齐排版，展示道具的六个极正视角。纯白色纯净背景，专业产品影棚摄影，标准六视图参考。六视图包括：绝对正前方视图、绝对正后方视图、绝对左侧视图、绝对右侧视图、绝对正上方俯拍视图、绝对正下方仰拍视图。所有视图必须是同一件道具，材质、颜色、比例、结构完全一致。使用超长焦镜头或移轴镜头效果，将透视变形降到最低，物体所有本该平行的边缘在画面中保持平行，接近正交投影。每个视图都像在专业产品影棚中用三脚架精密校准拍摄，构图绝对端正，物体在每个格子中居中，无任何倾斜、旋转或透视畸变。画面出不得出现任何人物、角色、人群、人影等；不得出现手、脚、人脸、场景、建筑、自然景观；无其他道具；必须无文字、无水印、无 logo、无 UI 元素，不要任何剧情事件，保持道具本体清晰、保持完整轮廓、保持所有角度的材质和结构一致。
6. 结尾以「[视觉风格：xxx]」点明该道具画风，与 style 字段风格一致。
完整示例（六视图模板段以「……」缩略，实际输出必须为上面模板全文）：道具名称：算筹。外观特征：由三十枚打磨光滑的细长方体长条组成。材质细节描述：采用深黑色乌木材质，表面刻有精细的度量刻度，侧面有长期使用的包浆光泽，每根长 15 厘米，宽 0.5 厘米。关键细节：其中几根算筹的末端刻有隐蔽的二十四节气小篆字样。高质量写实道具多角度展示图，……。[视觉风格：盛唐宫廷·古风悬疑写实·真人电影级写实]`;
const scriptGenerationStrategy: GenerationStrategy = {
    canHandle: (t) => t === NodeType.SCRIPT_NODE,
    buildRequest: (node, inputs, prompt) => {
        const script = (node.data as Partial<{ script: ScriptData }>).script;
        const isCharacterSource = script?.source === 'CHARACTER';
        // v1.4.34：补充分镜师角色设定，提升 LLM 对叙事连贯性约束的遵循度；
        // v1.4.35：输出 JSON 顶层新增 style 字段（全局视觉风格，随资产/单镜提示词注入生图保证全片画风统一）；
        // v1.4.36：style 提炼要求从 60 字扩到 100-200 字五维度（画风/色彩/光影/笔触材质/镜头质感），对齐参考产品的长描述结构；
        // v1.4.38：SHOT_GUIDELINES 新增「关键事件必入镜 + 因果链完整」两条硬约束（防开篇事件句被当背景跳过）
        const instruction = isCharacterSource
            ? `你是一名专业影视分镜师。请根据以下角色设定与描述，拆解为分镜脚本：输出 JSON，包含 style 字符串、shots 数组（每项含 duration、description、shotSize、lighting、dialogue、soundEffect、cameraMove）与 entities 对象（characters/scenes/props 数组，每项含 name、aliases、description）。其中 style 为从内容提炼的全局视觉风格描述（100-200字，依次涵盖五个维度：画风类型（如2D/3D/水墨/赛博朋克）、色彩体系（主色调与撞色关系）、光影质感（光源特性/体积光/丁达尔效应）、笔触与材质纹理、镜头画面质感（景深/颗粒感/清晰度），用逗号或顿号连成一段连贯描述，如"水墨淡彩，古意长卷叙事画卷，人物工笔写意，冷青灰与赭朱红撞色，笔触飘逸，以松散墨点与留白营造意境，画面带宣纸纹理质感，弱化透视强调平面装饰性"），全片所有画面统一遵循。${SHOT_GUIDELINES}${CHARACTER_DESC_GUIDELINES}${SCENE_DESC_GUIDELINES}${PROP_DESC_GUIDELINES}`
            : `你是一名专业影视分镜师。请根据以下剧本内容，生成分镜脚本：输出 JSON，包含 style 字符串、shots 数组（每项含 duration、description、shotSize、lighting、dialogue、soundEffect、cameraMove）与 entities 对象（characters/scenes/props 数组，每项含 name、aliases、description）。其中 style 为从剧本提炼的全局视觉风格描述（100-200字，依次涵盖五个维度：画风类型（如2D/3D/水墨/赛博朋克）、色彩体系（主色调与撞色关系）、光影质感（光源特性/体积光/丁达尔效应）、笔触与材质纹理、镜头画面质感（景深/颗粒感/清晰度），用逗号或顿号连成一段连贯描述，如"水墨淡彩，古意长卷叙事画卷，人物工笔写意，冷青灰与赭朱红撞色，笔触飘逸，以松散墨点与留白营造意境，画面带宣纸纹理质感，弱化透视强调平面装饰性"），全片所有画面统一遵循。${SHOT_GUIDELINES}${CHARACTER_DESC_GUIDELINES}${SCENE_DESC_GUIDELINES}${PROP_DESC_GUIDELINES}`;
        // 方案 §8-2：角色入口把上游图片类节点（MEDIA_UPLOAD/IMAGE_GENERATOR）的图片资产作为角色图传入（chat 多模态）；
        // v1.4.56 跳过 scriptAssetRef 资产生成节点（其连线仅表达资产归属，资产图不作分镜参考图，防语义污染）
        const inputImages = isCharacterSource
            ? collectInputImages(inputs.filter(n => !(n.data as { scriptAssetRef?: unknown } | undefined)?.scriptAssetRef))
            : [];
        return {
            mode: 'chat',
            feature: 'script-to-storyboard',
            prompt: [instruction, prompt].filter(Boolean).join('\n\n'),
            inputImages: inputImages.length > 0 ? inputImages : undefined,
        };
    },
    processResult: async (node, result) => {
        // v1.4.35：payload 顶层新增 style（全局视觉风格），LLM 未回时留空（ASSETS 页可手动补充）
        const payload = (result.data ?? {}) as { shots?: unknown[]; style?: unknown; entities?: { characters?: unknown[]; scenes?: unknown[]; props?: unknown[] } };
        const rawShots = Array.isArray(payload.shots) ? payload.shots : [];
        if (rawShots.length === 0) {
            throw new Error('未收到分镜脚本数据');
        }

        const skeleton = (node.data as Partial<{ script: ScriptData }>).script;
        const source: ScriptData['source'] = skeleton?.source === 'CHARACTER' ? 'CHARACTER' : 'SCRIPT';

        // id 与 entities 一致由前端统一生成（同次响应内 Date.now 相同但 i 递增，不重复），不信任 LLM 返回的 id 防重复冲突
        const shots: Shot[] = rawShots.map((raw: any, i: number) => ({
            id: `shot-${Date.now()}-${i}`,
            index: i + 1,
            duration: String(raw?.duration ?? ''),
            description: String(raw?.description ?? ''),
            shotSize: String(raw?.shotSize ?? ''),
            lighting: String(raw?.lighting ?? ''),
            dialogue: String(raw?.dialogue ?? ''),
            soundEffect: String(raw?.soundEffect ?? ''),
            cameraMove: String(raw?.cameraMove ?? ''),
            prompt: '',
            motionPrompt: '',
            promptStatus: 'PENDING' as const,
        }));

        const toAssets = (arr: unknown, kind: AssetKind): ScriptAsset[] =>
            (Array.isArray(arr) ? arr : []).map((raw: any, i: number) => ({
                id: `asset-${kind.toLowerCase()}-${Date.now()}-${i}`,
                kind,
                name: String(raw?.name ?? ''),
                description: String(raw?.description ?? ''),
                // v1.4.76 别名归一：解析 LLM 返回的 aliases（同一实体其他称谓），供高亮词典与
                // @-提及匹配；旧响应/缺失兜底空数组，非字符串项过滤
                aliases: Array.isArray(raw?.aliases)
                    ? raw.aliases.filter((x: unknown): x is string => typeof x === 'string' && x.trim() !== '').map((x: string) => x.trim())
                    : [],
                status: 'PENDING' as const,
            }));
        const entities = payload.entities ?? {};
        const assets = [
            ...toAssets(entities.characters, 'CHARACTER'),
            ...toAssets(entities.scenes, 'SCENE'),
            ...toAssets(entities.props, 'PROP'),
        ];

        const script: ScriptData = {
            dataVersion: 1,
            title: skeleton?.title || '分镜脚本',
            source,
            currentStep: 'SHOTS',
            shots,
            assets,
            style: String(payload.style ?? '').trim(),
            promptsGenerated: 0,
        };
        return { script };
    },
};

const strategies: GenerationStrategy[] = [
    imageGeneratorStrategy,
    videoGeneratorStrategy,
    audioGeneratorStrategy,
    imageEditorStrategy,
    panoramaGeneratorStrategy,
    scriptGenerationStrategy,
];

export const findStrategy = (nodeType: NodeType): GenerationStrategy | undefined => {
    return strategies.find(s => s.canHandle(nodeType));
};

export async function executeGeneration(
    node: AppNode,
    inputs: AppNode[],
    prompt: string,
    ctx: GenerationContext,
): Promise<Partial<AppNode['data']>> {
    const strategy = findStrategy(node.type);
    if (!strategy) throw new Error(`No strategy for node type: ${node.type}`);

    // 同步通道（TTS 等）：直接执行并返回节点更新，不创建异步任务
    if (strategy.executeSync) {
        return strategy.executeSync(node, inputs, prompt, ctx);
    }

    const request = strategy.buildRequest(node, inputs, prompt);
    // 注入 projectId / nodeId 到 request，让后端 TaskRecord 能记录任务归属
    // 用于前端重新进入项目时按 project_id 批量恢复运行中任务
    if (ctx.projectId) {
        request.projectId = ctx.projectId;
    }
    request.nodeId = node.id;

    const asyncResult = await generateApi.generateAsync(request);
    const taskId = asyncResult.taskId;

    ctx.onTaskCreated(taskId, node.id, node.type, request as Record<string, any>);

    const statusResult = await generateApi.pollTaskUntilComplete(taskId, (status) => {
        if (status.status === 'running') {
            ctx.onProgress(node.id, { progress: '\u751F\u6210\u4E2D...' });
        }
    });

    if (statusResult.status === 'failed') {
        throw new Error(statusResult.error || '\u4EFB\u52A1\u5931\u8D25');
    }
    if (statusResult.status === 'cancelled') {
        throw new Error('\u4EFB\u52A1\u5DF2\u53D6\u6D88');
    }

    const resultData = statusResult.result;
    if (!resultData) {
        throw new Error('\u672A\u6536\u5230\u751F\u6210\u7ED3\u679C');
    }

    const rawResult: GenerateResult = {
        taskId: statusResult.taskId,
        status: statusResult.status,
        results: resultData.results,
        error: resultData.error,
        videoMetadata: resultData.videoMetadata,
        analysis: resultData.analysis,
        // LLM 结构化产物通道（脚本节点等 feature 分支）：不透传则 strategy 拿不到 shots/entities
        data: resultData.data,
    };

    return strategy.processResult(node, rawResult, ctx, prompt);
}

export { imageGeneratorStrategy, videoGeneratorStrategy, audioGeneratorStrategy, imageEditorStrategy, panoramaGeneratorStrategy, scriptGenerationStrategy };
